import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Percent } from "lucide-react";

/**
 * AdminAirlineCommissions
 * Per-carrier commission % attached to AirTicketingReq → travels along with
 * the e-ticket and settles to the agency on the BSP cycle.
 * Some BSP carriers (MS, SV) require this element even at 0% — otherwise
 * Travelport rejects ticketing with "COMMISSION PERCENTAGE MUST BE ENTERED".
 */
export default function AdminAirlineCommissions() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/airline-commissions"); setRows(data.items || []); }
    catch (_e) { toast.error("Failed to load commissions"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit) return;
    if (!edit.airline_code || edit.airline_code.length < 2) { toast.error("IATA code required (e.g. MS, SV, EK)"); return; }
    if (edit.commission_pct === "" || Number.isNaN(Number(edit.commission_pct))) { toast.error("Commission % required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/airline-commissions", {
        airline_code: edit.airline_code.toUpperCase(),
        commission_pct: Number(edit.commission_pct),
        active: edit.active !== false,
        note: edit.note || null,
      });
      toast.success(`Commission saved for ${edit.airline_code.toUpperCase()}`);
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (code) => {
    if (!window.confirm(`Delete commission for ${code}?`)) return;
    try { await api.delete(`/admin/airline-commissions/${code}`); toast.success("Deleted"); load(); }
    catch (_e) { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-airline-commissions">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[var(--brand-accent)] flex items-center gap-2">
            <Percent className="w-5 h-5 text-[var(--brand-primary)]" /> Airline Commissions
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl leading-relaxed">
            Per-carrier <strong>commission %</strong> emitted as <code>&lt;air:Commission Type="PercentBase"&gt;</code> on every <code>AirTicketingReq</code>. BSP-cycle airlines (MS, SV, EK, etc.) settle this back to the agency on each invoice batch. Some carriers <strong>require the element even at 0%</strong>.
          </p>
        </div>
        <Button data-testid="airline-commission-add-btn" onClick={() => setEdit({ airline_code: "", commission_pct: "", active: true, note: "" })} className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
          <Plus className="w-4 h-4 mr-1" /> Add commission
        </Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Airline (IATA)</TableHead>
            <TableHead>Commission %</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Note</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.airline_code} data-testid={`comm-row-${r.airline_code}`}>
                <TableCell className="font-mono font-bold text-[var(--brand-accent)]">{r.airline_code}</TableCell>
                <TableCell className="font-mono">{Number(r.commission_pct).toFixed(2)}%</TableCell>
                <TableCell>{r.active ? <Badge className="bg-emerald-50 text-emerald-700 border-0">Active</Badge> : <Badge variant="outline">Disabled</Badge>}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-xs truncate">{r.note || "—"}</TableCell>
                <TableCell className="text-xs text-slate-400">{r.updated_at?.slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`comm-edit-${r.airline_code}`} size="sm" variant="ghost" onClick={() => setEdit({ ...r })}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button data-testid={`comm-del-${r.airline_code}`} size="sm" variant="ghost" onClick={() => del(r.airline_code)} className="text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No commissions configured yet. Click <strong className="text-[var(--brand-accent)]">Add commission</strong> to start.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent data-testid="airline-commission-dialog">
          <DialogHeader>
            <DialogTitle>{edit?.id ? `Edit ${edit.airline_code}` : "Add airline commission"}</DialogTitle>
            <DialogDescription>Pass-through % shipped with every ticket on this carrier. 0 still emits the element.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Airline IATA code</Label>
              <Input data-testid="commission-airline-code" value={edit?.airline_code || ""} onChange={(e) => setEdit({ ...edit, airline_code: e.target.value.toUpperCase().slice(0, 3) })} placeholder="MS / SV / EK" className="font-mono uppercase" />
            </div>
            <div>
              <Label>Commission %</Label>
              <Input data-testid="commission-pct" value={edit?.commission_pct ?? ""} onChange={(e) => setEdit({ ...edit, commission_pct: e.target.value })} type="number" step="0.01" placeholder="0.00" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input data-testid="commission-note" value={edit?.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} placeholder="e.g. EgyptAir BSP-EG contract effective 2026-Q1" />
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="commission-active" checked={edit?.active !== false} onCheckedChange={(v) => setEdit({ ...edit, active: v })} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="commission-save-btn" onClick={save} disabled={busy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
